import React from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

/**
 * Animation keyframes
 */
const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOutRight = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
`;

const slideOutUp = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-100%);
  }
`;

/**
 * Get animation based on position
 */
const getAnimation = (position, exiting) => {
  if (exiting) {
    return position.includes('top')
      ? css`
          ${slideOutUp} 0.3s ease-in forwards
        `
      : css`
          ${slideOutRight} 0.3s ease-in forwards
        `;
  }

  return position.includes('right')
    ? css`
        ${slideInRight} 0.3s ease-out
      `
    : css`
        ${slideInDown} 0.3s ease-out
      `;
};

/**
 * Toast Item Component
 */
const ToastItem = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-left: 4px solid
    ${({ type }) => {
      switch (type) {
        case 'success':
          return '#10b981';
        case 'error':
          return '#ef4444';
        case 'warning':
          return '#f59e0b';
        case 'info':
          return '#3b82f6';
        default:
          return '#6b7280';
      }
    }};
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  pointer-events: auto;
  animation: ${({ position, exiting }) => getAnimation(position, exiting)};
  min-width: 300px;
  max-width: 500px;

  @media (max-width: 640px) {
    min-width: 0;
    max-width: none;
  }
`;

/**
 * Icon Wrapper
 */
const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: ${({ type }) => {
    switch (type) {
      case 'success':
        return '#10b981';
      case 'error':
        return '#ef4444';
      case 'warning':
        return '#f59e0b';
      case 'info':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  }};
`;

/**
 * Content Wrapper
 */
const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

/**
 * Title
 */
const Title = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 14px;
  margin-bottom: ${({ hasMessage }) => (hasMessage ? '4px' : '0')};
`;

/**
 * Message
 */
const Message = styled.div`
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
  word-wrap: break-word;
`;

/**
 * Close Button
 */
const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 2px;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #6b7280;
    background: #f3f4f6;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

/**
 * Action Button
 */
const ActionButton = styled.button`
  margin-top: 8px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 4px;
  color: ${({ type }) => {
    switch (type) {
      case 'success':
        return '#10b981';
      case 'error':
        return '#ef4444';
      case 'warning':
        return '#f59e0b';
      case 'info':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  }};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ type }) => {
      switch (type) {
        case 'success':
          return '#10b981';
        case 'error':
          return '#ef4444';
        case 'warning':
          return '#f59e0b';
        case 'info':
          return '#3b82f6';
        default:
          return '#6b7280';
      }
    }};
    color: white;
  }

  &:focus {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

/**
 * Get icon component by type
 */
const getIcon = (type) => {
  switch (type) {
    case 'success':
      return CheckCircle;
    case 'error':
      return XCircle;
    case 'warning':
      return AlertCircle;
    case 'info':
      return Info;
    default:
      return Info;
  }
};

/**
 * Toast Component
 *
 * Individual toast notification with icon, title, message, and optional action
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Unique toast ID
 * @param {string} props.type - Toast type (success, error, warning, info)
 * @param {string} props.title - Toast title
 * @param {string} props.message - Toast message
 * @param {string} props.position - Toast position
 * @param {boolean} props.dismissible - Whether toast can be dismissed
 * @param {boolean} props.exiting - Whether toast is exiting
 * @param {Object} props.action - Optional action button { label, onClick }
 * @param {Function} props.onDismiss - Dismiss callback
 */
const Toast = ({
  id,
  type = 'info',
  title,
  message,
  position,
  dismissible = true,
  exiting = false,
  action,
  onDismiss,
}) => {
  const Icon = getIcon(type);

  return (
    <ToastItem
      type={type}
      position={position}
      exiting={exiting}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <IconWrapper type={type}>
        <Icon size={20} />
      </IconWrapper>

      <Content>
        {title && <Title hasMessage={!!message}>{title}</Title>}
        {message && <Message>{message}</Message>}
        {action && (
          <ActionButton
            type={type}
            onClick={() => {
              action.onClick();
              if (onDismiss) onDismiss(id);
            }}
          >
            {action.label}
          </ActionButton>
        )}
      </Content>

      {dismissible && onDismiss && (
        <CloseButton onClick={() => onDismiss(id)} aria-label="Dismiss">
          <X size={16} />
        </CloseButton>
      )}
    </ToastItem>
  );
};

export default Toast;
